import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { RequestTrackingResponse } from '@openapi';
import api from '@services/api';
import { TimeRange, computeDateRange } from './useTimeRange';

interface UseRequestsDataResult {
  requests: RequestTrackingResponse[];
  total: number;
  loading: boolean;
  refetch: () => void;
}

export const useRequestsData = (
  timeRange: TimeRange,
  customDateRange: [moment.Moment, moment.Moment] | null,
  viewMode: 'slowest' | 'errors',
  page: number,
  pageSize: number,
): UseRequestsDataResult => {
  const [requests, setRequests] = useState<RequestTrackingResponse[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchData = useCallback(async () => {
    const dateRange = computeDateRange(timeRange, customDateRange);
    if (!dateRange) return;

    setLoading(true);
    try {
      const requestsData = await api.tracking.getRequestsByMode({
        mode: viewMode,
        page: page - 1, // backend uses 0-based indexing
        pageSize,
        startTime: dateRange.startTime,
        endTime: dateRange.endTime,
      });

      if (requestsData?.data) {
        setRequests(requestsData.data);
        setTotal(requestsData.total ?? 0);
      }
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, customDateRange, viewMode, page, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { requests, total, loading, refetch: fetchData };
};
